import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";

const EMPTY = { name: "", email: "", password: "", role: "manager" };

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/users");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setEditing("new"); };
  const close = () => setEditing(null);

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/auth/users", form);
      toast.success("User created");
      close();
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/auth/users/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/auth/users/${id}`, { role }); toast.success("Role updated"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Admins can invite managers to use the billing suite."
        actions={<button onClick={openCreate} className="btn-primary flex items-center gap-1.5" data-testid="add-user-button"><Plus size={14} weight="bold" /> New User</button>}
      />

      <div className="p-8">
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-ink-muted">No users.</td></tr>}
              {rows.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-semibold">{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    <select className="field !py-1 !px-2 text-xs font-semibold uppercase w-32" value={u.role} onChange={e => changeRole(u.id, e.target.value)} data-testid={`role-select-${u.id}`}>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                    </select>
                  </td>
                  <td>{fmtDate(u.created_at)}</td>
                  <td className="text-right">
                    <button onClick={() => remove(u.id)} className="btn-ghost inline-flex items-center gap-1 text-destructive" data-testid={`delete-user-${u.id}`}><Trash size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing === "new" && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-lg border border-border max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="font-display text-lg font-semibold">New User</div>
              <button onClick={close} className="btn-ghost"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="label-overline block mb-1.5">Name *</div>
                <input className="field" data-testid="user-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Email *</div>
                <input type="email" className="field" data-testid="user-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Password *</div>
                <input type="text" className="field font-mono" data-testid="user-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Role</div>
                <select className="field" data-testid="user-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
              <button onClick={close} className="btn-secondary">Cancel</button>
              <button onClick={save} disabled={loading || !form.name || !form.email || !form.password} className="btn-primary" data-testid="save-user">{loading ? "Creating…" : "Create User"}</button>
            </div>
          </div>
        </div>
      )}

      <FieldStyle />
    </div>
  );
}
